/**
 * **The Ankiety tab's left-hand stream.** Every occurrence of every form the client was ever sent,
 * newest first, grouped by the month it stands in.
 *
 * Canon: `docs/design/ankiety-klienta-2026-09-16/szkice/kierunek-a.html` — the layout the owner
 * chose on 16 September 2026, over direction B's styling.
 *
 * ── It is the WHOLE history, and that is the point ───────────────────────────────────────────
 * No form filter beyond the chip the trainer pressed, and no date range at all: the range bar
 * narrows the charts, and a list that hid the occurrence the trainer is looking for because a date
 * field says „ostatnie 8 tygodni" is a list that cannot be scrolled back through. The read is
 * capped at 200 occurrences, so this is exactly as long as the read is.
 *
 * ── The INTAKE is a row like any other ───────────────────────────────────────────────────────
 * It is asked once, so it has no cadence and no ordinal — but it has answers, and they are
 * readable. It is kept out of a recurring form's comparison rather than out of the list: see
 * `compareGroup`, which is what a comparison may pair two rows across.
 *
 * ── A missed occurrence is drawn and cannot be opened ────────────────────────────────────────
 * „pominięta" is a fact about the client's week and belongs on the list. What it does not have is a
 * report: there are no answers, so the row is dimmed and is not a control.
 *
 * ── A RUN of missed occurrences is one row ───────────────────────────────────────────────────
 * The backend mints a row for every due date, including the days the client never opened the app,
 * so a daily check-in and a quiet fortnight is fourteen identical lines burying the answers either
 * side of them. Inside one month, a STRETCH of consecutive missed rows is folded form by form: a
 * form with three or more missed occurrences in it stands as one row — „pominięte ×5" over the span
 * of dates — where its newest one stood. Missed rows of another form do not break a form's run,
 * because two forms sent on the same days interleave by date under „Wszystkie"; anything that is
 * not missed does. Two stay two: a summary of two saves nothing and hides a date.
 *
 * ── An OPEN cycle is waiting, never missed ───────────────────────────────────────────────────
 * The server mints today's cycle before the client has seen it. It wears „czeka" in neutral ink
 * while it is its form's newest occurrence; which outcome an occurrence has is
 * `occurrenceOutcomeOf`, the same call the pane's columns make.
 *
 * Pure: no React, no DOM, no query hook, no i18next import. `t` and the locale arrive as
 * parameters.
 */

import {
  isOneOffKind,
  newestAnsweredOccurrenceId,
  occurrenceOutcomeOf,
  ordinalOf,
  orderedOccurrences,
  type SurveyHistorySource,
  type SurveyOccurrenceOutcome,
  type SurveyOccurrenceSource,
} from '~/features/survey-report';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * „po terminie" wears the warn soft chip; „pominięta" is a dashed outline carrying no colour; „czeka"
 * is the plain chip — a state, not news.
 */
export type SurveyStreamChipTone = 'warn' | 'ghost' | 'neutral';

/** The shortest run of missed occurrences that is drawn as one row. See the header. */
const COLLAPSED_RUN_MIN = 3;

export interface SurveyStreamFormChip {
  /** `null` is „Wszystkie". Otherwise the form's own key — see `SurveyStreamRow.chipId`. */
  readonly id: string | null;
  readonly title: string;
  readonly selected: boolean;
}

/**
 * One line of the stream: an occurrence, or a collapsed run of missed ones (see the header).
 *
 * A run is a row like a single missed occurrence is — not selectable, so drawn and never a control —
 * which is why it needs no shape of its own: its date is the span and its chip carries the count.
 */
export interface SurveyStreamRow {
  /**
   * The survey assignment id — the same key the pane, the report and the write are addressed by. A
   * collapsed run carries its newest occurrence's, which nothing addresses since it cannot be opened.
   */
  readonly id: number;
  readonly title: string;
  /**
   * „pon 14 wrz · tydz. 12", or just „pon 1 cze" where the form has no cadence to count. A collapsed
   * run's is its span, „20–24 wrz".
   */
  readonly whenLabel: string;
  /** „tydz. 12". `null` on an intake, which is asked once and has no position in a series. */
  readonly ordinalLabel: string | null;
  /** „po terminie" / „pominięta" / „czeka" / „pominięte ×5". `null` when it landed on time. */
  readonly chipLabel: string | null;
  readonly chipTone: SurveyStreamChipTone;
  /** It was answered, so a report of it exists: the row is a control and may be compared. */
  readonly selectable: boolean;
  readonly reviewed: boolean;
  /** Which chip shows this row. */
  readonly chipId: string;
  /**
   * The set a comparison may pair this row WITHIN.
   *
   * A recurring form's template, so any two of its occurrences may stand side by side. An intake is
   * alone in its own group: there is no second occurrence of it, and pairing it with a check-in
   * would compare two different question sets under one table.
   */
  readonly compareGroup: string;
}

export interface SurveyStreamMonth {
  readonly key: string;
  /** „wrzesień 2026" — the eyebrow, uppercased by the stylesheet as every other eyebrow is. */
  readonly label: string;
  readonly rows: readonly SurveyStreamRow[];
}

/**
 * What the tab needs to know about an occurrence it is not currently drawing.
 *
 * ── Why this is not read off the rendered rows ───────────────────────────────────────────────
 * A comparison is anchored to the FORM of the occurrence the trainer checked first, and the anchor
 * was being looked up in the rows the stream had drawn. Those rows are the chip's, not the
 * history's: with the anchor's own row filtered out by a form chip the lookup missed, the anchor
 * came back `null`, and every row of every other form became checkable — a table of two different
 * question sets, which is the one thing `compareGroup` exists to prevent. This map is every
 * occurrence in the read, whatever the chip says.
 */
export interface SurveyStreamEntry {
  /** See `SurveyStreamRow.compareGroup`. */
  readonly compareGroup: string;
  /** Its position in the whole history, oldest first. The pair is ordered by it. */
  readonly order: number;
  /** Its form is sent on a schedule, so it has a trend — see `chartStripOccurrenceId`. */
  readonly cadenced: boolean;
}

export interface SurveyStreamView {
  readonly chips: readonly SurveyStreamFormChip[];
  /** Every occurrence in the read, by id. See `SurveyStreamEntry`. */
  readonly index: ReadonlyMap<number, SurveyStreamEntry>;
  /** Newest month first, and newest row first inside each one. */
  readonly months: readonly SurveyStreamMonth[];
  /** How many occurrences are shown, after the chip — a collapsed run counts each of its own. */
  readonly total: number;
  /** How many of the SHOWN rows still wait for a review. The head's ember count. */
  readonly waitingCount: number;
  /** The client has never been sent a survey at all. */
  readonly empty: boolean;
  /** The chip is on a form with nothing in it. Only reachable from a stale chip. */
  readonly emptyForChip: boolean;
  /** The row the tab opens on — the newest ANSWERED occurrence of any form. */
  readonly defaultRowId: number | null;
}

export interface SurveyStreamDeps {
  readonly t: Translate;
  readonly locale: string;
  /** Which chip is pressed. `null` is „Wszystkie". */
  readonly chipId: string | null;
}

/** The key a form is filtered by. An occurrence with no template is its own form. */
function chipKeyOf(occurrence: SurveyOccurrenceSource): string {
  return occurrence.templateId === null
    ? `o${String(occurrence.id)}`
    : `t${String(occurrence.templateId)}`;
}

/** See `SurveyStreamRow.compareGroup`: a one-off form is alone even when it carries a template. */
function compareGroupOf(occurrence: SurveyOccurrenceSource): string {
  return isOneOffKind(occurrence.kind) || occurrence.templateId === null
    ? `o${String(occurrence.id)}`
    : `t${String(occurrence.templateId)}`;
}

/** The word a status wears in the stream, and `null` for the status that needs none. */
function statusChip(
  t: Translate,
  status: SurveyOccurrenceOutcome,
): { readonly label: string | null; readonly tone: SurveyStreamChipTone } {
  if (status === 'OnTime') return { label: null, tone: 'ghost' };
  return {
    label: t(`web:clientDetail.surveys.status.${status}`),
    // „po terminie" is a fact about the ANSWER and wears the warn soft chip; „pominięta" and
    // „odpięta" are the absence of one and wear the dashed outline that carries no colour; „czeka"
    // is neither, and wears the plain chip.
    tone: status === 'Late' ? 'warn' : status === 'Open' ? 'neutral' : 'ghost',
  };
}

export function buildSurveyStream(
  source: SurveyHistorySource | null,
  deps: SurveyStreamDeps,
): SurveyStreamView {
  const { t, locale, chipId } = deps;

  const empty: SurveyStreamView = {
    chips: [],
    index: new Map(),
    months: [],
    total: 0,
    waitingCount: 0,
    empty: true,
    emptyForChip: false,
    defaultRowId: null,
  };
  if (source === null || source.occurrences.length === 0) return empty;

  const ordered = orderedOccurrences(source.occurrences);

  /* ── The chips ────────────────────────────────────────────────────────────────────────────
   * One per form, in the order the forms were last heard from — the form the trainer is actually
   * running first. „Wszystkie" leads, because the stream's default is the whole history. */
  const lastSeen = new Map<string, SurveyOccurrenceSource>();
  for (const occurrence of ordered) lastSeen.set(chipKeyOf(occurrence), occurrence);
  // Sorted by each form's NEWEST occurrence, not by the order the map happened to be filled in: a
  // `Map` keeps first-insertion order, which is the order the forms were first heard from — the
  // opposite of what a trainer is looking for.
  const known = [...lastSeen.entries()].sort(([, left], [, right]) =>
    (right.cycleDate ?? '').localeCompare(left.cycleDate ?? ''),
  );
  const chosen = chipId !== null && lastSeen.has(chipId) ? chipId : null;
  const chips: SurveyStreamFormChip[] = [
    { id: null, title: t('web:clientDetail.surveys.allForms'), selected: chosen === null },
    ...known.map(([id, occurrence]) => ({
      id,
      title: occurrence.title,
      selected: id === chosen,
    })),
  ];

  /* ── The ordinal, per comparison group ────────────────────────────────────────────────────
   * Counted exactly as the pane's own columns count it — from the group's earliest occurrence in
   * the fetched list, stepped by the cadence its newest one was sent on. A group of one (an intake)
   * has no series to be a position in and gets none. */
  const groups = new Map<string, SurveyOccurrenceSource[]>();
  for (const occurrence of ordered) {
    const key = compareGroupOf(occurrence);
    const bucket = groups.get(key);
    if (bucket === undefined) groups.set(key, [occurrence]);
    else bucket.push(occurrence);
  }

  /* Both formatters read the SAME clock the month key is cut from. A `cycleDate` is a calendar day
   * with no zone, parsed here as midnight UTC — so a formatter left on the machine's zone printed
   * „sierpień 2026" over a row whose key was `2026-09` for anyone west of Greenwich, and the
   * eyebrow and the grouping disagreed about which month the row was in. */
  const whenFormat = new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
  const monthFormat = new Intl.DateTimeFormat(locale, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
  /** A collapsed run's span, „20–24 wrz". A run never crosses a month, so one month name does. */
  const spanFormat = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });

  /** The newest occurrence of each comparison group — the only one that can still be open. */
  const newestOfForm = new Set(
    [...groups.values()].flatMap((bucket) => {
      const newest = bucket.at(-1);
      return newest === undefined ? [] : [newest.id];
    }),
  );
  const outcomeOf = (occurrence: SurveyOccurrenceSource): SurveyOccurrenceOutcome =>
    occurrenceOutcomeOf(occurrence, newestOfForm.has(occurrence.id));
  /**
   * Missed and never answered: the only kind of row a fold is made of. The `Completed` check is not
   * redundant with the outcome — the wire types `status` and `occurrenceStatus` separately, and a
   * row carrying both „Missed" and an answer must stay openable rather than vanish into a fold.
   */
  const isMissed = (occurrence: SurveyOccurrenceSource): boolean =>
    occurrence.status !== 'Completed' && outcomeOf(occurrence) === 'Missed';

  /** The position of an occurrence in its own group's series — `model/surveyHistory`'s, so the
   * stream's row and the pane's column head cannot name one occurrence two ways. */
  const positionOf = (occurrence: SurveyOccurrenceSource): string | null =>
    ordinalOf(groups.get(compareGroupOf(occurrence)) ?? [], occurrence, t)?.label ?? null;

  /** Every occurrence in the read, whatever the chip shows. See `SurveyStreamEntry`. */
  const index = new Map<number, SurveyStreamEntry>(
    ordered.map((occurrence, order) => [
      occurrence.id,
      {
        compareGroup: compareGroupOf(occurrence),
        order,
        cadenced: !isOneOffKind(occurrence.kind) && occurrence.frequency !== null,
      },
    ]),
  );

  /* ── The rows, newest first ───────────────────────────────────────────────────────────────
   * An occurrence with no `cycleDate` cannot be placed in a month and is not drawn: the stream is
   * a calendar of what was asked, and a row with no date has no place in one. */
  const shown = [...ordered]
    .reverse()
    .filter(
      (occurrence) =>
        occurrence.cycleDate !== null && (chosen === null || chipKeyOf(occurrence) === chosen),
    );

  const dayOf = (occurrence: SurveyOccurrenceSource): Date =>
    new Date(`${occurrence.cycleDate ?? ''}T00:00:00Z`);

  const rowOf = (occurrence: SurveyOccurrenceSource): SurveyStreamRow => {
    const chip = statusChip(t, outcomeOf(occurrence));
    const ordinalLabel = positionOf(occurrence);
    const dateLabel = whenFormat.format(dayOf(occurrence));
    return {
      id: occurrence.id,
      title: occurrence.title,
      whenLabel:
        ordinalLabel === null
          ? dateLabel
          : t('web:clientDetail.surveys.streamWhen', { date: dateLabel, at: ordinalLabel }),
      ordinalLabel,
      chipLabel: chip.label,
      chipTone: chip.tone,
      selectable: occurrence.status === 'Completed',
      reviewed: occurrence.reviewedAt !== null,
      chipId: chipKeyOf(occurrence),
      compareGroup: compareGroupOf(occurrence),
    };
  };

  /** The run's span reads oldest to newest; the stream itself is newest first. */
  const collapsedRowOf = (
    newest: SurveyOccurrenceSource,
    oldest: SurveyOccurrenceSource,
    count: number,
  ): SurveyStreamRow => ({
    ...rowOf(newest),
    whenLabel: spanFormat.formatRange(dayOf(oldest), dayOf(newest)),
    ordinalLabel: null,
    chipLabel: t('web:clientDetail.surveys.missedRun', { count }),
    chipTone: 'ghost',
  });

  /* One pass groups the shown occurrences by month; a second walks each month and folds its runs.
   * Folding inside a month is what keeps a run from crossing an eyebrow. */
  const byMonth: { key: string; label: string; occurrences: SurveyOccurrenceSource[] }[] = [];
  let waitingCount = 0;
  for (const occurrence of shown) {
    if (occurrence.status === 'Completed' && occurrence.reviewedAt === null) waitingCount += 1;
    const key = (occurrence.cycleDate ?? '').slice(0, 7);
    const last = byMonth.at(-1);
    if (last !== undefined && last.key === key) {
      last.occurrences.push(occurrence);
    } else {
      byMonth.push({ key, label: monthFormat.format(dayOf(occurrence)), occurrences: [occurrence] });
    }
  }

  /** One stretch of consecutive missed rows, folded form by form. See the header. */
  const foldStretch = (stretch: readonly SurveyOccurrenceSource[]): SurveyStreamRow[] => {
    const byForm = new Map<string, SurveyOccurrenceSource[]>();
    for (const occurrence of stretch) {
      const key = chipKeyOf(occurrence);
      const bucket = byForm.get(key);
      if (bucket === undefined) byForm.set(key, [occurrence]);
      else bucket.push(occurrence);
    }
    return stretch.flatMap((occurrence) => {
      const run = byForm.get(chipKeyOf(occurrence)) ?? [];
      if (run.length < COLLAPSED_RUN_MIN) return [rowOf(occurrence)];
      const [newest] = run;
      const oldest = run.at(-1);
      // The fold stands where its newest member stood; the rest of the run is inside it.
      if (occurrence !== newest || oldest === undefined) return [];
      return [collapsedRowOf(newest, oldest, run.length)];
    });
  };

  const months: SurveyStreamMonth[] = byMonth.map(({ key, label, occurrences }) => {
    const rows: SurveyStreamRow[] = [];
    let stretch: SurveyOccurrenceSource[] = [];
    for (const occurrence of occurrences) {
      if (isMissed(occurrence)) {
        stretch.push(occurrence);
        continue;
      }
      rows.push(...foldStretch(stretch), rowOf(occurrence));
      stretch = [];
    }
    rows.push(...foldStretch(stretch));
    return { key, label, rows };
  });

  return {
    chips,
    index,
    months,
    total: shown.length,
    waitingCount,
    empty: false,
    emptyForChip: shown.length === 0,
    defaultRowId: newestAnsweredOccurrenceId(source),
  };
}
